import { useLocation } from "wouter";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Edit } from "lucide-react";
import { formatCurrency } from "@/lib/currency";
import { useAuth } from "@/lib/auth-context";

export default function ViewStarterIngredient() {
  const [location, setLocation] = useLocation();
  const [ingredientId, setIngredientId] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');
    setIngredientId(id);
  }, [location]);

  const { data: ingredientsData, isLoading: ingredientsLoading } = useQuery({
    queryKey: ["/api/starter-pack/ingredients"],
  });

  const { data: categoriesData } = useQuery({
    queryKey: ["/api/starter-pack/categories/ingredients"],
  });

  const ingredients = Array.isArray(ingredientsData) ? ingredientsData : [];
  const categories = Array.isArray(categoriesData) ? categoriesData : [];

  const ingredient = ingredientId && Array.isArray(ingredients)
    ? ingredients.find((i: any) => i._id === ingredientId || i.id === ingredientId)
    : undefined;

  const category = ingredient?.categoryId && Array.isArray(categories)
    ? categories.find((c: any) => c._id === ingredient.categoryId)?.name || 'Uncategorized'
    : 'Uncategorized';

  if (ingredientsLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!ingredient) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => setLocation("/starter-pack")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-xl sm:text-3xl font-bold">Ingredient not found</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => setLocation("/starter-pack")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl sm:text-3xl font-bold tracking-tight">{ingredient.name}</h1>
            <p className="text-muted-foreground">Starter Pack Ingredient</p>
          </div>
        </div>
        <Button onClick={() => setLocation(`/starter-pack/ingredient?edit=${ingredient._id || ingredient.id}`)}>
          <Edit className="h-4 w-4 mr-2" />
          Edit
        </Button>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Name</label>
              <p className="text-lg">{ingredient.name}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Category</label>
              <p className="text-lg">{category}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Unit</label>
              <p className="text-lg">{ingredient.unit || 'N/A'}</p>
            </div>
            {ingredient.isCountBased && (
              <>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Type</label>
                  <p className="text-lg">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Count-Based
                    </span>
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Purchase Unit</label>
                  <p className="text-lg">{ingredient.purchaseUnit || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Pieces per Purchase Unit</label>
                  <p className="text-lg">{ingredient.piecesPerPurchaseUnit || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Weight per Piece</label>
                  <p className="text-lg">{ingredient.weightPerPiece ? `${ingredient.weightPerPiece}g` : 'N/A'}</p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Pricing Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Purchase Amount</label>
              <p className="text-lg">{formatCurrency(Number(ingredient.purchaseAmount || 0), user?.currency || "USD")}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Price per Gram</label>
              <p className="text-lg">{formatCurrency(Number(ingredient.pricePerGram || 0), user?.currency || "USD")}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {(ingredient.image || ingredient.imageUrl) && (
        <Card>
          <CardHeader>
            <CardTitle>Image</CardTitle>
          </CardHeader>
          <CardContent>
            <img
              src={ingredient.image || ingredient.imageUrl}
              alt={ingredient.name}
              className="w-full max-w-md mx-auto rounded-lg object-cover"
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
